import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft, Home, Building2, Car, Heart, Bot, TrendingUp,
  BarChart3, ShieldCheck, Settings, LogOut, User, ShoppingBag,
  Sparkles, Menu, X, ChevronDown, Crown, Shield
} from 'lucide-react';
import { useState } from 'react';
import CurrencySelector from '@/components/CurrencySelector';
import { Logo } from '@/components/Logo';
import { getStoredUser, clearAuth } from '@/utils/auth';
import PartnerNotifications from '@/components/PartnerNotifications';

const CUSTOMER_LINKS = [
  { path: '/dashboard', label: 'Dashboard', testId: 'nav-dashboard-link' },
  { path: '/properties', label: 'Properties', testId: 'nav-properties-link' },
  { path: '/marketplace', label: 'Marketplace', testId: 'nav-marketplace-link' },
  { path: '/wishlist', label: 'Wishlist', testId: 'nav-wishlist-link' },
  { path: '/concierge', label: 'Concierge', testId: 'nav-concierge-link' },
];

const MOBILE_ICONS = {
  '/dashboard': Home,
  '/properties': Building2,
  '/marketplace': ShoppingBag,
  '/wishlist': Heart,
  '/concierge': Bot,
};

/**
 * Consolidated Nav component for ASTIIV BASGAT platform.
 * 
 * Props:
 * - variant: 'customer' (default) | 'partner' | 'public'
 * - showBack: boolean (show back button)
 * - activePage: string (current page label or path for highlight)
 * - partnerLabel: string (e.g., "Developer Portal")
 * - companyName: string
 * - tier: string (user subscription tier)
 * - isAdmin: boolean
 * - onLogout: function
 * - rightContent: JSX (optional custom right-side content)
 * - className: string (additional classes)
 */
const Nav = ({
  variant = 'customer',
  showBack = false,
  activePage = '',
  partnerLabel = '',
  companyName = '',
  tier = 'free',
  isAdmin = false,
  onLogout,
  rightContent,
  className = '',
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout: authLogout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isActive = (path, label) => {
    if (activePage) {
      return activePage === path || activePage === label || activePage.toLowerCase() === label.toLowerCase();
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Resolve where to send the user after they click Logout.
  // Partners → their **regional** partner landing (e.g. /in/partner or /uae/partner).
  // Customers → /login (unchanged).
  const resolveLogoutDestination = () => {
    if (variant !== 'partner') return '/login';
    // 1. Prefer the bound region on the stored partner profile.
    try {
      const partner = getStoredUser();
      const region = (partner?.region || '').toUpperCase();
      if (region === 'IN') return '/in/partner';
      if (region === 'UAE') return '/uae/partner';
    } catch { /* ignore */ }
    // 2. No localStorage fallback — region selection must be explicit on
    //    re-entry per ASTIIV BASGAT product mandate (2026-02-27). Logged-out
    //    visitors always re-pick from the canonical RegionSelector.
    return '/partner';
  };

  const handleLogout = () => {
    const dest = resolveLogoutDestination();
    if (variant === 'partner') {
      // Always purge partner sessionStorage before navigating away.
      try { clearAuth(); } catch { /* ignore */ }
    }
    authLogout();
    if (onLogout) onLogout();
    navigate(dest, { replace: true });
  };

  // Build links based on variant and tier
  const getLinks = () => {
    if (variant === 'partner') return [];
    if (variant === 'public') return [];

    const links = [...CUSTOMER_LINKS];

    // Conditional links based on tier
    if (tier !== 'free') {
      links.splice(1, 0, { path: '/portfolio', label: 'Portfolio', testId: 'nav-portfolio-link', icon: Sparkles });
    }
    if (isAdmin || tier === 'premium' || tier === 'elite') {
      links.push({ path: '/investor', label: 'Investor', testId: 'nav-investor-link' });
    }
    if (isAdmin) {
      links.push({ path: '/analytics', label: 'Analytics', testId: 'nav-analytics-link' });
      links.push({ path: '/admin', label: 'Admin', testId: 'nav-admin-link' });
    }

    return links;
  };

  const links = getLinks();

  if (variant === 'partner') {
    // Resolve the partner's bound region for the pill display.
    const partner = (() => { try { return getStoredUser(); } catch { return null; } })();
    const region = (partner?.region || '').toUpperCase();
    const regionPill = region === 'IN'
      ? { flag: '🇮🇳', label: 'India', tone: 'text-orange-300 ring-orange-500/30 bg-orange-500/5' }
      : region === 'UAE'
      ? { flag: '🇦🇪', label: 'UAE', tone: 'text-emerald-300 ring-emerald-500/30 bg-emerald-500/5' }
      : null;

    return (
      <nav className={`glass-card border-b border-white/5 sticky top-0 z-50 backdrop-blur-xl ${className}`} data-testid="partner-nav">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            {showBack && (
              <Button variant="ghost" onClick={() => navigate(-1)} className="text-text-secondary hover:text-gold" data-testid="nav-back-button">
                <ArrowLeft className="w-4 h-4 mr-2" /> <span className="hidden sm:inline">Back</span>
              </Button>
            )}
            <Logo to="/" size="sm" wordmarkClassName="text-gold" testId="partner-nav-logo" />
            {partnerLabel && (
              <>
                <span className="text-text-secondary hidden sm:inline">|</span>
                <span className="text-text-secondary hidden sm:inline text-sm">{partnerLabel}</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-3 sm:gap-4">
            {regionPill && (
              <span
                className={`hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] font-semibold ring-1 ${regionPill.tone}`}
                title={`You are operating in ${regionPill.label}`}
                data-testid="nav-region-pill"
              >
                <span aria-hidden className="text-sm leading-none">{regionPill.flag}</span>
                {regionPill.label}
              </span>
            )}
            {companyName && <span className="text-text-secondary text-sm hidden md:inline">{companyName}</span>}
            <PartnerNotifications />
            <Link to="/partner/account" className="text-text-secondary hover:text-gold" data-testid="nav-partner-account">
              <User className="w-4 h-4" />
            </Link>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="border-white/15 text-white/90 hover:bg-red-500/10 hover:text-red-300 hover:border-red-500/40"
              data-testid="nav-logout-btn"
            >
              <LogOut className="w-4 h-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </nav>
    );
  }

  if (variant === 'public') {
    return (
      <nav className={`glass-card border-b border-white/5 sticky top-0 z-50 ${className}`} data-testid="public-nav">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 flex justify-between items-center">
          <div className="flex items-center gap-4">
            {showBack && (
              <Button variant="ghost" onClick={() => navigate(-1)} className="text-text-secondary hover:text-gold" data-testid="nav-back-button">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
            )}
            <Logo to="/" size="md" wordmarkClassName="text-gold" testId="public-nav-logo" />
          </div>
          {rightContent || (
            <div className="flex items-center gap-4">
              <Link to="/login" className="text-text-secondary hover:text-gold transition-colors text-sm">Sign In</Link>
              <Link to="/partner/login" className="text-text-secondary hover:text-gold transition-colors text-sm">Partner</Link>
            </div>
          )}
        </div>
      </nav>
    );
  }

  // Customer variant (default)
  return (
    <>
    <nav className={`glass-card border-b border-white/5 sticky top-0 z-50 ${className}`} data-testid="customer-nav">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-3 lg:py-6 flex justify-between items-center">
        {/* Left: Back + Logo */}
        <div className="flex items-center gap-2 sm:gap-4">
          {showBack && (
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-text-secondary hover:text-gold p-1.5 lg:p-2" data-testid="nav-back-button">
              <ArrowLeft className="w-4 h-4 lg:mr-2" /> <span className="hidden lg:inline">Back</span>
            </Button>
          )}
          <Logo to="/dashboard" size="md" wordmarkClassName="text-gold" testId="customer-nav-logo" />
        </div>

        {/* Desktop Links */}
        <div className="hidden lg:flex items-center gap-4 xl:gap-6 flex-1 justify-center mx-4">
          {links.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`transition-colors flex items-center gap-1.5 text-base font-medium ${
                isActive(link.path, link.label) ? 'text-gold' : 'text-text-secondary hover:text-gold'
              }`}
              data-testid={link.testId}
            >
              {link.icon && <link.icon className="w-4 h-4" />}
              {link.label}
            </Link>
          ))}
        </div>

        {/* Right: Desktop user menu + Mobile controls */}
        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          <div className="hidden sm:block">
            <CurrencySelector />
          </div>
          {rightContent}
          
          {/* Desktop User Dropdown */}
          <div className="hidden lg:block relative group">
            <button className="flex items-center gap-2 px-3 py-2 rounded-lg text-text-secondary hover:text-gold hover:bg-white/5 transition-colors" data-testid="nav-user-menu-btn">
              <User className="w-5 h-5" />
              <ChevronDown className="w-3 h-3" />
            </button>
            <div className="absolute right-0 top-full mt-2 w-52 py-2 rounded-xl bg-[#1a1a1a] border border-white/15 shadow-[0_8px_30px_rgba(0,0,0,0.6)] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-[100]">
              <Link to="/account" className="flex items-center gap-3 px-5 py-3 text-sm text-text-secondary hover:text-gold hover:bg-white/5 transition-colors" data-testid="nav-account-link">
                <User className="w-4 h-4" /> My Account
              </Link>
              <Link to="/settings" className="flex items-center gap-3 px-5 py-3 text-sm text-text-secondary hover:text-gold hover:bg-white/5 transition-colors" data-testid="nav-settings-link">
                <Settings className="w-4 h-4" /> Settings
              </Link>
              {isAdmin && (
                <Link to="/admin" className="flex items-center gap-3 px-5 py-3 text-sm text-gold hover:bg-gold/10 transition-colors" data-testid="nav-profile-admin-link">
                  <Shield className="w-4 h-4" /> Admin Panel
                </Link>
              )}
              <div className="border-t border-white/10 my-1.5 mx-3" />
              <button onClick={handleLogout} className="w-full flex items-center gap-3 px-5 py-3 text-sm text-red-400 hover:bg-red-500/10 transition-colors" data-testid="nav-logout-btn">
                <LogOut className="w-4 h-4" /> Logout
              </button>
            </div>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden p-2 text-text-secondary hover:text-gold"
            data-testid="nav-mobile-toggle"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-white/5 bg-obsidian/95 backdrop-blur-xl">
          <div className="max-w-[1440px] mx-auto px-4 py-3 space-y-1">
            {links.map((link) => {
              const Icon = MOBILE_ICONS[link.path] || Home;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                    isActive(link.path, link.label) ? 'text-gold bg-gold/10' : 'text-text-secondary hover:text-gold hover:bg-white/5'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {link.label}
                </Link>
              );
            })}
            <div className="border-t border-white/10 pt-2 mt-2 space-y-1">
              <div className="px-4 py-2 sm:hidden">
                <CurrencySelector />
              </div>
              <Link to="/subscription" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl text-text-secondary hover:text-gold hover:bg-white/5">
                <Crown className="w-4 h-4" /> Subscription
              </Link>
              <Link to="/settings" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl text-text-secondary hover:text-gold hover:bg-white/5">
                <Settings className="w-4 h-4" /> Settings
              </Link>
              {isAdmin && (
                <Link to="/admin" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl text-gold hover:bg-gold/10" data-testid="nav-mobile-admin-link">
                  <Shield className="w-4 h-4" /> Admin Panel
                </Link>
              )}
              <button onClick={() => { setMobileOpen(false); handleLogout(); }} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-red-400 hover:bg-red-500/10" data-testid="nav-mobile-logout-btn">
                <LogOut className="w-4 h-4" /> Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
    </>
  );
};

export default Nav;
